export type ImageRole = 'detail' | 'body-context'

export type BodyRegion =
  | 'testa'
  | 'collo'
  | 'torace'
  | 'schiena'
  | 'addome'
  | 'braccio'
  | 'avambraccio'
  | 'mano'
  | 'coscia'
  | 'gamba'
  | 'piede'
  | 'non-riconosciuta'

export type Side = 'sinistro' | 'destro' | 'centrale' | 'non-specificato'

export type RecognitionStatus = 'local-proposed' | 'manual' | 'not-recognized'

export interface Position {
  region: BodyRegion
  side: Side
  status: RecognitionStatus
  note?: string
}

export interface Archive {
  id: string
  personId: string
  personName: string
  formatVersion: 1
  createdAt: string
  updatedAt: string
  nextNeoNumber: number
  appVersion: string
}

export interface Neo {
  id: string
  archiveId: string
  number: number
  label?: string
  position: Position
  createdAt: string
  updatedAt: string
}

export interface Observation {
  id: string
  archiveId: string
  neoId: string
  shotAt: string | null
  importedAt: string
  shotOffsetMinutes: number | null
  note?: string
  gridGuide?: GridGuide
  createdAt: string
}

export interface GridGuide {
  referenceMm: number
  previewSpacingPx: number
  offsetXPx?: number
  offsetYPx?: number
}

export interface ImageAsset {
  id: string
  archiveId: string
  observationId: string
  role: ImageRole
  blob: Blob
  mimeType: string
  byteSize: number
  sha256: string
  width: number | null
  height: number | null
  createdAt: string
}

export interface Calibration {
  id: string
  archiveId: string
  observationId: string
  p1: Point
  p2: Point
  distanceMm: number
  pixelsPerMm: number
  method: 'physical-reference-two-marks'
  createdAt: string
}

export interface Measurement {
  id: string
  archiveId: string
  observationId: string
  calibrationId: string
  p1: Point
  p2: Point
  diameterMm: number
  method: 'manual-diameter-two-points'
  createdAt: string
}

export interface Point {
  x: number
  y: number
}

export interface ArchiveBundle {
  formatVersion: 1
  exportedAt: string
  archive: Archive
  nevi: Neo[]
  observations: Observation[]
  calibrations: Calibration[]
  measurements: Measurement[]
  images: ExportedImage[]
}

export interface ArchiveCollectionBundle {
  formatVersion: 2
  exportedAt: string
  archives: ArchiveBundle[]
}

export type BackupPayload = ArchiveBundle | ArchiveCollectionBundle

export interface ExportedImage {
  id: string
  archiveId: string
  observationId: string
  role: ImageRole
  mimeType: string
  byteSize: number
  sha256: string
  width: number | null
  height: number | null
  createdAt: string
  dataUrl: string
}

export interface PersistedState {
  archive: Archive
  archives: Archive[]
  nevi: Neo[]
  observations: Observation[]
  images: ImageAsset[]
  calibrations: Calibration[]
  measurements: Measurement[]
}

export const BODY_REGIONS: Array<{ value: BodyRegion; label: string }> = [
  { value: 'testa', label: 'Testa / viso' },
  { value: 'collo', label: 'Collo' },
  { value: 'torace', label: 'Torace' },
  { value: 'schiena', label: 'Schiena' },
  { value: 'addome', label: 'Addome' },
  { value: 'braccio', label: 'Braccio' },
  { value: 'avambraccio', label: 'Avambraccio' },
  { value: 'mano', label: 'Mano' },
  { value: 'coscia', label: 'Coscia' },
  { value: 'gamba', label: 'Gamba' },
  { value: 'piede', label: 'Piede' },
  { value: 'non-riconosciuta', label: 'Non riconosciuta' },
]

export const SIDES: Array<{ value: Side; label: string }> = [
  { value: 'sinistro', label: 'Sinistro' },
  { value: 'destro', label: 'Destro' },
  { value: 'centrale', label: 'Centro' },
  { value: 'non-specificato', label: 'Non specificato' },
]
